// Tour View Page - Display tour details
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { useTours } from '../../tour/hooks/useTours';
import { Tour } from '../../tour/types';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';

const TourViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedMuseum = useAppSelector(selectSelectedMuseum);

  const { data: tours } = useTours();

  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User

  useEffect(() => {
    if (tours && id) {
      const foundTour = tours.find(t => t.id === parseInt(id));
      if (foundTour) {
        setTour(foundTour);
      } else {
        setError('Tour not found');
      }
      setLoading(false);
    }
  }, [tours, id]);

  const handleEdit = () => {
    navigate(`/tours/${id}/edit`);
  };

  const handleDelete = () => {
    // TODO: Implement delete functionality
    console.log('Delete tour:', id);
  };

  const handleBack = () => {
    navigate('/tours');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tour) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Tour not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Tours
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
          <Typography variant="h4">Tour {tour.tour_id}</Typography>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
              Edit
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Tour Details */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tour Details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Tour ID
                    </Typography>
                    <Typography variant="body1">{tour.tour_id}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{tour.description || 'No description'}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1">{tour.duration} minutes</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={tour.is_active ? 'Active' : 'Inactive'}
                      color={tour.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Museum
                    </Typography>
                    <Typography variant="body1">{tour.museum_code || `Museum ID: ${tour.museum}`}</Typography>
                  </Box>
                </Grid>
                {tour.museum_level && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        Museum Level
                      </Typography>
                      <Typography variant="body1">
                        {tour.museum_level === 'B' ? 'Basement' : tour.museum_level}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Waypoint Map */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Waypoint Map
              </Typography>
              <iframe
                src={`${process.env.REACT_APP_RAAS_BASE_URL || 'https://nhb-raas-staging.senserbot.com'}/api-ws/routing/?hide=true&showNames=true&scale=90&museum=${encodeURIComponent(selectedMuseum?.museum_code || tour.museum_code || String(tour.museum))}`}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '920px',
                  border: 'none',
                }}
                title="Waypoint Routing Map"
                scrolling="yes"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Waypoints */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tour Route ({tour.waypoint_lists?.length || 0} waypoints)
              </Typography>
              {tour.waypoint_lists && tour.waypoint_lists.length > 0 ? (
                <List>
                  {tour.waypoint_lists.map((waypoint, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ${waypoint.waypoint_name}`}
                          secondary={`Content: ${waypoint.content_name} (v${waypoint.version})`}
                          primaryTypographyProps={{ sx: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
                          secondaryTypographyProps={{ sx: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
                        />
                      </ListItem>
                      {index < tour.waypoint_lists.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No waypoints configured
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Metadata */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Metadata
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">{new Date(tour.created_at).toLocaleString()}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">{new Date(tour.updated_at).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TourViewPage;
